/**
 * CloudManager.ts
 * Gestionnaire du stockage cloud et de la synchronisation
 * Refactorisé pour utiliser un backend réel au lieu de mocks
 */
import { logger } from './utils/ProfessionalLogger';
import {
  validateProjectData,
  validateCanvasData,
  type ProjectData,
  CanvasDataSchema,
} from './types/ProjectData';
import type { z } from 'zod';
import { safeSetJSONToStorage } from './utils/JSONValidator';
import { compressJSON, decompressJSON } from './utils/CompressionUtils';
import { VersionManager } from './utils/VersionManager';
import { AppError } from './utils/AppError';
import { supabase, isSupabaseConfigured } from './services/SupabaseService';
import {
  getProjectUploadPath,
  validateStoragePath,
  generateUuidFilename,
} from './utils/storagePaths';
import { formatStoragePathAsUrl } from './services/StorageUrlService';
import { StorageService } from './services/StorageService';

// Réexporter ProjectData pour que les autres modules puissent l'utiliser
export type { ProjectData };

export interface StorageUsage {
  used: number;
  total: number;
  percentage: number;
}

export interface CloudConfig {
  autoSave: boolean;
  autoSaveInterval: number;
  maxProjects: number;
  storageLimit: number;
  enableCompression: boolean;
  enableVersioning: boolean;
  enableAutoBackup: boolean;
  backupInterval: number;
}
export class CloudManager {
  private projects: Map<string, ProjectData> = new Map();
  private config: CloudConfig;
  private autoSaveTimer: NodeJS.Timeout | null = null;
  private backupTimer: NodeJS.Timeout | null = null;
  private isInitialized: boolean = false;

  /**
   * Vérifie si Supabase est disponible et configuré
   */
  private ensureSupabaseAvailable(): void {
    if (!isSupabaseConfigured() || !supabase) {
      throw new AppError('SUPABASE_NOT_CONFIGURED', "Supabase n'est pas configuré", {});
    }
  }

  /**
   * Vérifie si l'utilisateur est authentifié
   */
  private async ensureAuthenticated(): Promise<string> {
    this.ensureSupabaseAvailable();
    const {
      data: { user },
      error: userError,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    } = await supabase!.auth.getUser();

    if (userError || !user) {
      throw new AppError('AUTH_ERROR', 'Utilisateur non authentifié', {});
    }

    return user.id;
  }

  constructor(config: Partial<CloudConfig> = {}) {
    this.config = {
      autoSave: true,
      autoSaveInterval: 30000, // 30 secondes;
      maxProjects: 100,
      storageLimit: 100 * 1024 * 1024, // 100MB;
      enableCompression: true,
      enableVersioning: true,
      enableAutoBackup: true,
      backupInterval: 300000, // 5 minutes;
      ...config,
    };
  }

  /**
   * Initialise le CloudManager;
   */
  public initialize(): void {
    if (this.isInitialized) {
      logger.warn('CloudManager d\u00e9j\u00e0 initialis\u00e9');
      return;
    }
    this.loadProjectsFromStorage();
    this.startAutoSave();
    if (this.config.enableAutoBackup) {
      this.startAutoBackup();
    }
    this.isInitialized = true;
    logger.info('\u2601\ufe0f CloudManager initialis\u00e9');
  }

  /**
   * Extrait et valide les paramètres de saveProject
   * @param dataOrProjectId - Les données du projet ou l'ID du projet
   * @param data - Les données du projet (si le premier param est un ID)
   * @param name - Le nom du projet (optionnel)
   * @returns Paramètres validés et normalisés
   * @throws AppError si les données sont invalides
   */
  private parseSaveProjectParams(
    dataOrProjectId: string | z.infer<typeof CanvasDataSchema>,
    data?: z.infer<typeof CanvasDataSchema>,
    name?: string
  ): { projectId: string; canvasData: z.infer<typeof CanvasDataSchema>; name?: string } {
    // Déterminer les paramètres selon la signature utilisée
    if (typeof dataOrProjectId === 'string' && data !== undefined) {
      // Appel avec (projectId, data, name?)
      const validation = validateCanvasData(data);
      if (!validation.success) {
        throw new AppError('VALIDATION_ERROR', `Données du canvas invalides: ${validation.error}`, {
          projectId: dataOrProjectId,
        });
      }
      return {
        projectId: dataOrProjectId,
        canvasData: validation.data,
        name,
      };
    } else {
      // Appel avec (data) - générer un ID automatiquement
      const validation = validateCanvasData(dataOrProjectId);
      if (!validation.success) {
        throw new AppError(
          'VALIDATION_ERROR',
          `Données du canvas invalides: ${validation.error}`,
          {}
        );
      }
      return {
        projectId: this.generateProjectId(),
        canvasData: validation.data,
        name: undefined,
      };
    }
  }

  /**
   * Calcule la version du projet selon la configuration
   * @param projectId - ID du projet
   * @returns Version du projet
   */
  private calculateProjectVersion(projectId: string): string {
    const existingProject = this.projects.get(projectId);
    const currentVersion = existingProject?.version ?? '1.0.0';

    if (!this.config.enableVersioning) {
      return currentVersion;
    }

    const newVersion = VersionManager.incrementVersion(currentVersion, 'patch');

    // Sauvegarder la version dans l'historique
    if (newVersion) {
      VersionManager.saveVersionHistory(projectId, {
        version: newVersion,
        timestamp: Date.now(),
        description: 'Sauvegarde automatique',
      });
    }

    return newVersion;
  }

  /**
   * Crée un objet ProjectData validé à partir des paramètres
   * @param params - Paramètres validés
   * @param projectType - Type de projet
   * @returns ProjectData validé
   */
  private createProjectData(
    params: { projectId: string; canvasData: z.infer<typeof CanvasDataSchema>; name?: string },
    projectType: 'template' | 'project' | 'template_editor'
  ): ProjectData {
    const { projectId, canvasData, name } = params;
    const existingProject = this.projects.get(projectId);
    const version = this.calculateProjectVersion(projectId);

    return {
      id: projectId,
      name: name || `Projet ${projectId}`,
      data: structuredClone(canvasData), // Deep clone pour éviter les mutations
      createdAt: existingProject?.createdAt ?? Date.now(),
      updatedAt: Date.now(),
      version,
      project_type: projectType,
    };
  }

  /**
   * Sauvegarde le projet dans Supabase avec gestion d'erreurs granulaire
   * @param project - Projet à sauvegarder
   * @param projectId - ID du projet
   * @param projectType - Type de projet
   * @throws AppError si la sauvegarde échoue
   */
  private async saveProjectToSupabase(
    project: ProjectData,
    projectId: string,
    projectType: 'template' | 'project' | 'template_editor'
  ): Promise<void> {
    const userId = await this.ensureAuthenticated();

    // Uploader les assets (Blob URLs) vers Supabase Storage
    // Cette opération est non-bloquante : on continue même si elle échoue
    try {
      logger.info('📤 Traitement des assets du projet avant sauvegarde...');
      await this.uploadAssetsToSupabaseStorage(project.data, projectId, userId);
      logger.info('✅ Assets traités avec succès');
    } catch (assetError) {
      logger.error('⚠️ Erreur lors du traitement des assets, sauvegarde sans upload:', assetError);
      // Continuer la sauvegarde même si l'upload des assets échoue
    }

    // Sauvegarder dans Supabase
    if (!supabase) {
      throw new AppError('SUPABASE_ERROR', 'Supabase client non disponible', { projectId });
    }

    const { error: supabaseError } = await supabase.from('template_editor_projects').upsert({
      id: projectId,
      user_id: userId,
      name: project.name,
      // P3 : cast canvas_data (typage Supabase JSON très large).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      canvas_data: project.data as any,
      project_type: projectType,
      updated_at: new Date().toISOString(),
    });

    if (supabaseError) {
      throw new AppError('SUPABASE_ERROR', `Erreur Supabase: ${supabaseError.message}`, {
        projectId,
        error: supabaseError,
      });
    }

    logger.info(`💾 Projet sauvegardé sur Supabase: ${project.name}`);
  }

  /**
   * Sauvegarde un projet (backend ou localStorage selon le mode)
   *
   * @deprecated Utiliser UnifiedSaveService.save() à la place
   * REFACTORISÉ : Typage strict, validation Zod, gestion d'erreurs granulaire
   *
   * @param dataOrProjectId - Les données du canvas validées ou l'ID du projet
   * @param data - Les données du canvas (si le premier param est un ID)
   * @param name - Le nom du projet (optionnel)
   * @param projectType - Le type de projet : 'template' | 'project' | 'template_editor' (optionnel, défaut: 'template_editor')
   * @throws AppError si la sauvegarde échoue ou si les données sont invalides
   */
  public async saveProject(
    dataOrProjectId: string | z.infer<typeof CanvasDataSchema>,
    data?: z.infer<typeof CanvasDataSchema>,
    name?: string,
    projectType: 'template' | 'project' | 'template_editor' = 'template_editor'
  ): Promise<void> {
    try {
      // Étape 1 : Parser et valider les paramètres
      const params = this.parseSaveProjectParams(dataOrProjectId, data, name);

      // Étape 2 : Créer l'objet ProjectData validé
      const project = this.createProjectData(params, projectType);

      // Étape 3 : Sauvegarder dans Supabase avec fallback gracieux
      try {
        await this.saveProjectToSupabase(project, params.projectId, projectType);
      } catch (supabaseError) {
        // Fallback gracieux : sauvegarder dans localStorage en cas d'erreur Supabase
        logger.warn('Erreur Supabase, fallback sur localStorage:', supabaseError);
        this.projects.set(params.projectId, project);
        this.saveProjectsToStorage();

        // Propager l'erreur pour informer l'utilisateur, mais les données sont sauvegardées localement
        if (supabaseError instanceof AppError) {
          throw supabaseError;
        }
        throw new AppError(
          'SAVE_ERROR',
          'Erreur lors de la sauvegarde cloud, données sauvegardées localement',
          { projectId: params.projectId },
          supabaseError instanceof Error ? supabaseError : new Error(String(supabaseError))
        );
      }

      // Étape 4 : Toujours mettre à jour le cache local (backup)
      this.projects.set(params.projectId, project);
      this.saveProjectsToStorage();
    } catch (error) {
      // Gestion d'erreurs granulaire avec contexte
      logger.error('Erreur lors de la sauvegarde du projet:', error);

      if (error instanceof AppError) {
        throw error;
      }

      throw new AppError(
        'SAVE_ERROR',
        'Erreur lors de la sauvegarde du projet',
        { dataOrProjectId, data, name },
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }
  /**
   * Charge un projet depuis Supabase
   * @deprecated Utiliser UnifiedSaveService.load() à la place
   * @param projectId - L'ID du projet à charger
   * @returns Le projet chargé ou null si non trouvé
   */
  public async loadProject(projectId: string): Promise<ProjectData | null> {
    try {
      const userId = await this.ensureAuthenticated();

      // Charger depuis Supabase
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const { data, error } = await supabase!
        .from('template_editor_projects')
        .select('*')
        .eq('id', projectId)
        .eq('user_id', userId)
        .single();

      if (error || !data) {
        logger.warn(`⚠️ Projet non trouvé sur Supabase: ${projectId}`);
        // Essayer de charger depuis le cache local comme fallback
        return this.loadProjectFromLocal(projectId);
      }

      // Convertir le format Supabase en ProjectData
      // Type guard pour valider la structure des données Supabase
      interface SupabaseProjectRow {
        id: string;
        name: string;
        canvas_data?: Record<string, unknown>;
        created_at: string;
        updated_at: string;
        project_type?: 'template' | 'project' | 'template_editor';
        [key: string]: unknown;
      }
      const projectData = data as SupabaseProjectRow;
      const project: ProjectData = {
        id: projectData.id,
        name: projectData.name,
        data: projectData.canvas_data || {},
        createdAt: new Date(projectData.created_at).getTime(),
        updatedAt: new Date(projectData.updated_at).getTime(),
        version: '1.0.0',
        project_type: projectData.project_type || 'template_editor', // Rétrocompatibilité
      };

      // Mettre à jour le cache local
      this.projects.set(projectId, project);
      logger.info(`📂 Projet chargé depuis Supabase: ${project.name}`);
      return project;
    } catch (error) {
      logger.error(`❌ Erreur lors du chargement du projet ${projectId}:`, error);
      // En cas d'erreur d'authentification ou autre, essayer le cache local
      if (error instanceof AppError && error.code === 'AUTH_ERROR') {
        return this.loadProjectFromLocal(projectId);
      }
      return this.loadProjectFromLocal(projectId);
    }
  }

  /**
   * Charge un projet depuis le stockage local (cache ou localStorage)
   */
  private loadProjectFromLocal(projectId: string): ProjectData | null {
    // Mode offline: charger depuis localStorage uniquement
    const cachedProject = this.projects.get(projectId);
    if (cachedProject) {
      logger.info(`📂 Projet chargé localement: ${projectId}`);
      return cachedProject;
    }

    // Essayer de charger depuis localStorage si pas dans le cache
    this.loadProjectsFromStorage();
    const project = this.projects.get(projectId);
    if (project) {
      logger.info(`📂 Projet chargé depuis localStorage: ${projectId}`);
      return project;
    }

    logger.warn(`⚠️ Projet non trouvé localement: ${projectId}`);
    return null;
  }
  /**
   * Obtient tous les projets depuis Supabase
   * @param type - Filtrer par type de projet : 'template' | 'project' | undefined (tous)
   * @returns Liste de tous les projets (filtrés par type si fourni)
   */
  public async getAllProjects(type?: 'template' | 'project'): Promise<ProjectData[]> {
    try {
      const userId = await this.ensureAuthenticated();

      // Charger depuis Supabase
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      let query = supabase!
        .from('template_editor_projects')
        .select('id, name, thumbnail_url, updated_at, created_at, project_type')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      // Filtrer par type si fourni, sinon inclure tous les types (template_editor inclus pour rétrocompatibilité)
      if (type) {
        query = query.eq('project_type', type);
      } else {
        // Par défaut, inclure tous les types pour rétrocompatibilité
        query = query.in('project_type', ['template', 'project', 'template_editor']);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Erreur lors du chargement des projets depuis Supabase:', error);
        // Fallback sur cache local
        this.loadProjectsFromStorage();
        return Array.from(this.projects.values());
      }

      // Convertir le format Supabase en ProjectData
      interface SupabaseProjectListItem {
        id: string;
        name: string;
        created_at: string;
        updated_at: string;
        [key: string]: unknown;
      }
      // P3 : cast `unknown[]` → SupabaseProjectListItem[] (Supabase type union large).
      const projects: ProjectData[] = ((data ?? []) as unknown as SupabaseProjectListItem[]).map(
        item => ({
          id: item.id,
          name: item.name,
          data: {}, // Les données complètes seront chargées via loadProject
          createdAt: new Date(item.created_at).getTime(),
          updatedAt: new Date(item.updated_at).getTime(),
          version: '1.0.0',
          project_type: (item.project_type as 'template' | 'project') || 'template_editor',
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ) as any;

      logger.info(`📂 ${projects.length} projets chargés depuis Supabase`);
      return projects;
    } catch (error) {
      logger.error('Erreur lors du chargement des projets:', error);
      // En cas d'erreur, charger depuis le cache local
      this.loadProjectsFromStorage();
      return Array.from(this.projects.values());
    }
  }
  /**
   * Supprime un projet depuis Supabase
   * @param projectId - L'ID du projet à supprimer
   * @returns true si supprimé, false si non trouvé
   */
  public async deleteProject(projectId: string): Promise<boolean> {
    try {
      const userId = await this.ensureAuthenticated();

      // Supprimer les assets associés
      await this.deleteProjectAssets(projectId, userId);

      // Supprimer dans Supabase
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const { error } = await supabase!
        .from('template_editor_projects')
        .delete()
        .eq('id', projectId)
        .eq('user_id', userId);

      if (error) {
        logger.error(`Erreur lors de la suppression du projet ${projectId}:`, error);
        // Essayer de supprimer localement comme fallback
        return this.deleteProjectFromLocal(projectId);
      }

      // Supprimer du cache local
      this.projects.delete(projectId);
      this.saveProjectsToStorage();

      logger.info(`🗑️ Projet supprimé de Supabase: ${projectId}`);
      return true;
    } catch (error) {
      logger.error(`❌ Erreur lors de la suppression du projet ${projectId}:`, error);
      // En cas d'erreur, essayer de supprimer localement
      return this.deleteProjectFromLocal(projectId);
    }
  }

  /**
   * Supprime un projet depuis le stockage local
   */
  private deleteProjectFromLocal(projectId: string): boolean {
    if (this.projects.has(projectId)) {
      this.projects.delete(projectId);
      this.saveProjectsToStorage();
      logger.info(`🗑️ Projet supprimé localement: ${projectId}`);
      return true;
    }
    logger.warn(`⚠️ Projet non trouvé localement: ${projectId}`);
    return false;
  }

  /**
   * Supprime les assets d'un projet depuis Supabase Storage
   * PHASE 2: Adapté pour la nouvelle structure avec sous-dossiers (uploads/, thumbnails/, exports/)
   */
  private async deleteProjectAssets(projectId: string, userId: string): Promise<void> {
    try {
      if (!supabase) {
        logger.warn('Supabase non disponible, impossible de supprimer les assets');
        return;
      }

      // PHASE 2: Lister les fichiers dans chaque sous-dossier (uploads, thumbnails, exports)
      const buckets = [
        'template-editor-uploads',
        'template-editor-thumbnails',
        'template-editor-exports',
      ];
      const subfolders = ['uploads', 'thumbnails', 'exports'];
      const allFilePaths: Array<{ bucket: string; path: string }> = [];

      for (let i = 0; i < buckets.length; i++) {
        const bucket = buckets[i];
        const subfolder = subfolders[i];
        const folderPath = `${userId}/${projectId}/${subfolder}`;

        const { data: files, error: listError } = await supabase.storage
          .from(bucket)
          .list(folderPath);

        if (listError) {
          logger.warn(
            `Erreur lors de la liste des fichiers dans ${bucket}/${folderPath}:`,
            listError
          );
          continue;
        }

        if (files && files.length > 0) {
          // Construire les paths complets avec le sous-dossier
          const folderFilePaths = files
            .filter(file => !file.name.endsWith('/')) // Exclure les dossiers
            .map(file => ({ bucket, path: `${folderPath}/${file.name}` }));
          allFilePaths.push(...folderFilePaths);
        }
      }

      if (allFilePaths.length === 0) {
        return;
      }

      // PHASE 2: Utiliser StorageService pour supprimer les fichiers
      // Grouper par bucket pour optimiser les appels
      const filesByBucket = new Map<string, string[]>();
      for (const { bucket, path } of allFilePaths) {
        if (!filesByBucket.has(bucket)) {
          filesByBucket.set(bucket, []);
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        filesByBucket.get(bucket)!.push(path);
      }

      // Supprimer tous les fichiers par bucket
      for (const [bucket, paths] of filesByBucket.entries()) {
        try {
          await StorageService.deleteFile(bucket, paths);
        } catch (error) {
          logger.error(
            `Erreur lors de la suppression des assets du bucket ${bucket} pour le projet ${projectId}:`,
            error
          );
        }
      }

      logger.info(`🗑️ ${allFilePaths.length} assets supprimés pour le projet ${projectId}`);
    } catch (error) {
      logger.error(`Erreur lors de la suppression des assets:`, error);
    }
  }
  /**
   * Exporte un projet depuis Supabase
   * @param projectId - L'ID du projet à exporter
   * @returns JSON stringifié du projet
   * @throws AppError si le projet n'est pas trouvé ou si la requête échoue
   */
  public async exportProject(projectId: string): Promise<string> {
    try {
      // Charger depuis Supabase
      const project = await this.loadProject(projectId);

      if (!project) {
        throw new AppError('EXPORT_ERROR', `Projet non trouvé: ${projectId}`, { projectId });
      }

      const exportData = {
        ...project,
        exportedAt: Date.now(),
      };
      return JSON.stringify(exportData, null, 2);
    } catch (error) {
      logger.error(`❌ Erreur lors de l'export du projet ${projectId}:`, error);
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'EXPORT_ERROR',
        `Erreur lors de l'export du projet: ${projectId}`,
        { projectId },
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }
  /**
   * Importe un projet dans Supabase
   * @param projectData - JSON stringifié du projet à importer
   * @returns Le projet importé
   * @throws AppError si la validation ou l'import échoue
   */
  public async importProject(projectData: string): Promise<ProjectData> {
    try {
      // Valider les données
      const validation = validateProjectData(projectData);
      if (!validation.success) {
        throw new AppError(
          'VALIDATION_ERROR',
          `Erreur lors de la validation du projet: ${validation.error}`,
          { validationError: validation.error }
        );
      }

      const project = validation.data;
      // CORRIGÉ: Générer un UUID valide si l'ID n'existe pas
      if (
        !project.id ||
        !project.id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i)
      ) {
        project.id = this.generateProjectId();
      }
      project.createdAt = Date.now();
      project.updatedAt = Date.now();

      // Sauvegarder dans Supabase (utilise la méthode saveProject existante)
      await this.saveProject(project.id, project.data, project.name);

      logger.info(`📥 Projet importé: ${project.name}`);
      return project;
    } catch (error) {
      logger.error("Erreur lors de l'import du projet:", error);
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'IMPORT_ERROR',
        "Erreur lors de l'import du projet",
        { projectData },
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }
  /**
   * Obtient l'utilisation du stockage;
   */
  public getStorageUsage(): Promise<StorageUsage> {
    return new Promise(resolve => {
      const dataString = JSON.stringify(Array.from(this.projects.values()));
      const used = new Blob([dataString]).size;
      const total = this.config.storageLimit;
      const percentage = (used / total) * 100;
      resolve({
        used,
        total,
        percentage: Math.round(percentage * 100) / 100,
      });
    });
  }
  /**
   * Vide tous les projets;
   */
  public clearAllProjects(): Promise<void> {
    return new Promise(resolve => {
      this.projects.clear();
      this.saveProjectsToStorage();
      logger.info('\ud83e\uddf9 Tous les projets supprim\u00e9s');
      resolve();
    });
  }
  /**
   * D\u00e9marre la sauvegarde automatique;
   */
  public startAutoSave(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
    }
    if (this.config.autoSave) {
      this.autoSaveTimer = setInterval(() => {
        this.performAutoSave();
      }, this.config.autoSaveInterval);
    }
  }
  /**
   * Arr\u00eate la sauvegarde automatique;
   */
  public stopAutoSave(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }
  /**
   * Effectue une sauvegarde automatique;
   * RENDU PUBLIC pour être utilisé par le hook React
   */
  public performAutoSave(): void {
    logger.info('\ud83d\udd04 Sauvegarde automatique...');
    // Ici on pourrait sauvegarder l'\u00e9tat actuel de l'application;
  }
  /**
   * Sauvegarde les projets dans le localStorage;
   */
  private saveProjectsToStorage(): void {
    const projectsArray = Array.from(this.projects.values());
    // P3 : un array est utilisé comme valeur, on cast pour matcher.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let dataToSave: any = projectsArray;

    // Appliquer la compression si activée
    if (this.config.enableCompression) {
      const jsonString = JSON.stringify(projectsArray);
      const compressed = compressJSON(jsonString);
      dataToSave = { compressed: true, data: compressed };
      const ratio = Math.round((1 - compressed.length / jsonString.length) * 100);
      if (ratio > 0) {
        logger.debug(`Compression: ${ratio}% d'économie`);
      }
    }

    const result = safeSetJSONToStorage('snapbooth_projects', dataToSave);
    if (!result.success) {
      logger.error('Erreur lors de la sauvegarde des projets:', result.error);
      if (result.error?.includes('quota')) {
        logger.warn('⚠️ Quota de stockage dépassé, suppression des anciens projets');
        this.cleanupOldProjects();
      }
    }
  }

  /**
   * Nettoie les anciens projets pour libérer de l'espace
   */
  private cleanupOldProjects(): void {
    const projectsArray = Array.from(this.projects.values());
    // Trier par date de mise à jour (plus récents en premier)
    projectsArray.sort((a, b) => b.updatedAt - a.updatedAt);
    // Garder seulement les 50 plus récents
    const projectsToKeep = projectsArray.slice(0, 50);
    this.projects.clear();
    projectsToKeep.forEach(p => this.projects.set(p.id, p));
    // Réessayer la sauvegarde
    const result = safeSetJSONToStorage('snapbooth_projects', projectsToKeep);
    if (!result.success) {
      logger.error('❌ Impossible de sauvegarder même après nettoyage');
    }
  }
  /**
   * Charge les projets depuis le localStorage;
   */
  private loadProjectsFromStorage(): void {
    const stored = localStorage.getItem('snapbooth_projects');
    if (!stored) {
      logger.info('Aucun projet sauvegardé');
      return;
    }

    try {
      const parsed = JSON.parse(stored);
      let projectsArray: ProjectData[];

      // Vérifier si les données sont compressées
      if (parsed.compressed && parsed.data) {
        const decompressed = decompressJSON(parsed.data);
        projectsArray = JSON.parse(decompressed);
      } else {
        projectsArray = parsed;
      }

      if (Array.isArray(projectsArray)) {
        this.projects = new Map(projectsArray.map(p => [p.id, p]));
        logger.info(
          `\ud83d\udcc2 ${projectsArray.length} projets charg\u00e9s depuis le stockage local`
        );
      } else {
        logger.warn('⚠️ Format de données invalide dans le stockage');
      }
    } catch (error) {
      logger.error('Erreur lors du chargement des projets:', error);
    }
  }

  /**
   * Démarre le backup automatique
   * NEUTRALISÉ: setInterval remplacé par hook React useCloudManagerAutoSave
   */
  private startAutoBackup(): void {
    if (this.backupTimer) {
      clearInterval(this.backupTimer);
      this.backupTimer = null;
    }
    // NEUTRALISÉ: setInterval supprimé - géré par hook React
    // Le hook useCloudManagerAutoSave remplace cette fonctionnalité
    logger.info(
      'ℹ️ Backup automatique CloudManager: géré par hook React (useCloudManagerAutoSave)'
    );
  }

  /**
   * Arrête le backup automatique
   */
  public stopAutoBackup(): void {
    if (this.backupTimer) {
      clearInterval(this.backupTimer);
      this.backupTimer = null;
    }
  }

  /**
   * Effectue un backup de tous les projets
   * RENDU PUBLIC pour être utilisé par le hook React
   */
  public performBackup(): void {
    try {
      const projectsArray = Array.from(this.projects.values());
      const backupData = {
        timestamp: Date.now(),
        projects: projectsArray,
        version: '1.0.0',
      };
      const backupKey = `snapbooth_backup_${Date.now()}`;
      const result = safeSetJSONToStorage(backupKey, backupData);
      if (result.success) {
        logger.info('💾 Backup automatique effectué');
        // Nettoyer les anciens backups (garder seulement les 5 derniers)
        this.cleanupOldBackups();
      } else {
        logger.warn('⚠️ Échec du backup automatique');
      }
    } catch (error) {
      logger.error('Erreur lors du backup automatique:', error);
    }
  }

  /**
   * Nettoie les anciens backups
   */
  private cleanupOldBackups(): void {
    try {
      const backupKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('snapbooth_backup_')) {
          backupKeys.push(key);
        }
      }

      // Trier par timestamp (plus récents en premier)
      backupKeys.sort((a, b) => {
        const timestampA = parseInt(a.replace('snapbooth_backup_', ''));
        const timestampB = parseInt(b.replace('snapbooth_backup_', ''));
        return timestampB - timestampA;
      });

      // Supprimer les backups au-delà de 5
      if (backupKeys.length > 5) {
        backupKeys.slice(5).forEach(key => {
          localStorage.removeItem(key);
        });
        logger.debug(`🧹 ${backupKeys.length - 5} anciens backups supprimés`);
      }
    } catch (error) {
      logger.error('Erreur lors du nettoyage des backups:', error);
    }
  }
  /**
   * Upload les assets (Blob URLs) vers Supabase Storage
   * @param projectData - Les données du projet
   * @param projectId - L'ID du projet
   * @param userId - L'ID de l'utilisateur
   */
  private async uploadAssetsToSupabaseStorage(
    projectData: ProjectData['data'],
    projectId: string,
    userId: string
  ): Promise<void> {
    // Trouver toutes les Blob URLs dans projectData
    const blobUrls = this.extractBlobUrls(projectData);

    for (const blobUrl of blobUrls) {
      try {
        // Convertir Blob URL en File
        const file = await this.blobUrlToFile(blobUrl);

        // PHASE 2: Utiliser chemins standardisés avec UUID
        const fileExtension = file.name.split('.').pop() || 'bin';
        const filename = generateUuidFilename(fileExtension);
        const storagePath = getProjectUploadPath(userId, projectId, filename);

        // Valider le chemin
        if (!validateStoragePath(storagePath, 'uploads')) {
          logger.error(`Chemin de stockage invalide: ${storagePath}`);
          continue;
        }

        // PHASE 2: Utiliser StorageService au lieu de supabase.storage directement
        try {
          const { storage_path: uploadedPath } = await StorageService.uploadFile(
            'template-editor-uploads',
            storagePath,
            file,
            file.type
          );

          // Utiliser le path retourné (peut être différent si upsert)
          const finalStoragePath = uploadedPath || storagePath;

          // PHASE 2: Stocker storage_path au lieu de signed URL
          // La signed URL sera générée à la demande lors du rendu avec cache
          const storagePathUrl = formatStoragePathAsUrl(
            finalStoragePath,
            'template-editor-uploads'
          );

          // Enregistrer les métadonnées dans template_editor_assets
          if (supabase) {
            const { error: assetError } = await supabase.from('template_editor_assets').insert({
              user_id: userId,
              project_id: projectId,
              storage_path: finalStoragePath,
              filename: file.name,
              size_bytes: file.size,
              mime_type: file.type,
            });

            if (assetError) {
              logger.warn(
                `Erreur lors de l'enregistrement des métadonnées de l'asset:`,
                assetError
              );
              // Ne pas bloquer, l'upload est réussi
            }
          }

          // Remplacer blob: par storage_path formaté dans projectData
          this.replaceBlobUrlInData(projectData, blobUrl, storagePathUrl);

          logger.info(`✅ Asset uploadé: ${file.name} -> ${finalStoragePath}`);
        } catch (uploadError) {
          logger.error(`Erreur lors de l'upload de ${file.name}:`, uploadError);
          continue;
        }
      } catch (error) {
        logger.error(`Erreur lors du traitement de la Blob URL ${blobUrl}:`, error);
        // Continuer avec les autres assets
      }
    }
  }

  /**
   * Extrait toutes les Blob URLs d'un objet récursivement
   */
  private extractBlobUrls(obj: unknown): string[] {
    const blobUrls: string[] = [];

    if (obj === null || obj === undefined) {
      return blobUrls;
    }

    if (typeof obj === 'string' && obj.startsWith('blob:')) {
      blobUrls.push(obj);
      return blobUrls;
    }

    if (Array.isArray(obj)) {
      obj.forEach(item => {
        blobUrls.push(...this.extractBlobUrls(item));
      });
      return blobUrls;
    }

    if (typeof obj === 'object') {
      Object.values(obj).forEach(value => {
        blobUrls.push(...this.extractBlobUrls(value));
      });
      return blobUrls;
    }

    return blobUrls;
  }

  /**
   * Convertit une Blob URL en File
   */
  private async blobUrlToFile(blobUrl: string): Promise<File> {
    const response = await fetch(blobUrl);
    if (!response.ok) {
      throw new Error(`Impossible de récupérer la Blob URL: ${blobUrl}`);
    }
    const blob = await response.blob();
    const extension = blob.type.split('/')[1] || 'png';
    const fileName = `asset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${extension}`;
    return new File([blob], fileName, { type: blob.type });
  }

  /**
   * Remplace une Blob URL par une URL Supabase dans les données
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private replaceBlobUrlInData(obj: any, oldUrl: string, newUrl: string): void {
    if (obj === null || obj === undefined) {
      return;
    }

    if (typeof obj === 'string' && obj === oldUrl) {
      // Cette fonction modifie l'objet en place, donc on ne peut pas remplacer directement
      // On doit utiliser une approche différente
      return;
    }

    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        if (typeof item === 'string' && item === oldUrl) {
          obj[index] = newUrl;
        } else {
          this.replaceBlobUrlInData(item, oldUrl, newUrl);
        }
      });
      return;
    }

    if (typeof obj === 'object') {
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        if (typeof value === 'string' && value === oldUrl) {
          obj[key] = newUrl;
        } else {
          this.replaceBlobUrlInData(value, oldUrl, newUrl);
        }
      });
      return;
    }
  }

  /**
   * Génère un ID de projet unique (UUID valide pour Supabase)
   * CORRIGÉ: Génère un UUID valide au lieu d'un ID personnalisé
   * P0.5 FIX: Rendu public pour être utilisé par TemplateDashboard
   */
  public generateProjectId(): string {
    // CORRIGÉ: Générer un UUID valide pour Supabase
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback pour les navigateurs qui ne supportent pas crypto.randomUUID
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
  /**
   * Obtient la configuration;
   */
  public getConfig(): CloudConfig {
    return { ...this.config };
  }
  /**
   * Met \u00e0 jour la configuration;
   */
  public updateConfig(newConfig: Partial<CloudConfig>): void {
    this.config = { ...this.config, ...newConfig };
    if (newConfig.autoSave !== undefined) {
      if (newConfig.autoSave) {
        this.startAutoSave();
      } else {
        this.stopAutoSave();
      }
    }
  }
  /**
   * V\u00e9rifie si le CloudManager est initialis\u00e9;
   */
  public isCloudInitialized(): boolean {
    return this.isInitialized;
  }
  /**
   * Définit les gestionnaires pour la synchronisation
   * REFACTORING: Types concrets au lieu de types non typés
   */
  public setManagers(
    _canvasManager: { toJSON(): string } | null | undefined,
    _historyManager: { saveState(data: unknown): void } | null | undefined
  ): void {
    // Cette méthode peut être utilisée pour connecter les gestionnaires
    logger.info('🔗 Gestionnaires connectés au CloudManager');
  }
  /**
   * Nettoyage des ressources;
   */
  public destroy(): void {
    this.stopAutoSave();
    this.isInitialized = false;
    logger.info('\ud83e\uddf9 CloudManager d\u00e9truit');
  }
}
